// Which version of each public ConfirmPH policy is in force, and which
// earlier versions remain readable.
//
// A new version never overwrites an old one. When a policy changes:
//   1. copy content/legal.jsx verbatim into content/legalArchive/v<date>.jsx
//      (it becomes the frozen text of the version being retired);
//   2. edit content/legal.jsx;
//   3. add the new version at the top of that policy's `versions` list and
//      give the retired one its `archive` key.
// The backend legal bundle (backend/compliance LEGAL_BUNDLE_VERSION) is bumped
// with it, so existing acceptances stay recorded against the version each
// person actually accepted.
//
// Version labels are the effective date. ConfirmPH does not number versions
// it cannot evidence: text earlier than 27 August 2026 is not kept here, so
// no number is invented for it.

export const ARCHIVES = {
  "2026-08-27": () => import("./legalArchive/v2026-08-27"),
};

const V_2026_09_18 = { version: "2026-09-18", effective: "2026-09-18" };
const V_2026_08_27 = { version: "2026-08-27", effective: "2026-08-27", archive: "2026-08-27" };

// `registryDocType` links a site policy to the Task 2 Legal Documents
// register. If the register ever publishes that document, the Policy Centre
// shows the register's version instead.
export const POLICIES = [
  { slug: "terms", path: "/terms", exportName: "TERMS", title: "Terms of Service",
    audience: "Everyone", registryDocType: null, versions: [V_2026_09_18, V_2026_08_27] },
  { slug: "privacy", path: "/privacy", exportName: "PRIVACY", title: "Privacy Policy",
    audience: "Everyone", registryDocType: "privacy_notice", versions: [V_2026_09_18, V_2026_08_27] },
  { slug: "cookies", path: "/cookies", exportName: "COOKIES", title: "Cookie Policy",
    audience: "Everyone", registryDocType: "cookie_notice", versions: [V_2026_09_18, V_2026_08_27] },
  { slug: "customer-terms", path: "/customer-terms", exportName: "CUSTOMER_TERMS",
    title: "Customer / Renter Terms", audience: "Renters", registryDocType: "renter_terms",
    versions: [V_2026_09_18, V_2026_08_27] },
  { slug: "business-terms", path: "/business-terms", exportName: "BUSINESS_TERMS",
    title: "Business Terms", audience: "Rental businesses", registryDocType: "business_terms",
    versions: [V_2026_09_18, V_2026_08_27] },
  { slug: "acceptable-use", path: "/acceptable-use", exportName: "ACCEPTABLE_USE",
    title: "Acceptable Use Policy", audience: "Everyone", registryDocType: "acceptable_use",
    versions: [V_2026_09_18, V_2026_08_27] },
  { slug: "refund", path: "/refund-policy", exportName: "REFUND", title: "Refund & Billing Policy",
    audience: "Rental businesses", registryDocType: "subscription_refund",
    versions: [V_2026_09_18, V_2026_08_27] },
  { slug: "trust-safety", path: "/trust-safety", exportName: "TRUST_SAFETY", title: "Trust & Safety",
    audience: "Everyone", registryDocType: "safety_verification", versions: [V_2026_09_18, V_2026_08_27] },
  { slug: "enhanced-safety", path: "/enhanced-safety", exportName: "ENHANCED_SAFETY",
    title: "Enhanced Vehicle Safety Enforcement", audience: "Everyone", registryDocType: null,
    versions: [V_2026_09_18, V_2026_08_27] },
  { slug: "reviews-policy", path: "/reviews-policy", exportName: "REVIEWS_POLICY",
    title: "Reviews Policy", audience: "Everyone", registryDocType: "review_policy",
    versions: [V_2026_09_18, V_2026_08_27] },
  { slug: "data-deletion", path: "/data-deletion", exportName: "DATA_DELETION",
    title: "Account & Data Deletion", audience: "Everyone", registryDocType: null,
    versions: [V_2026_09_18, V_2026_08_27] },
  { slug: "disclaimer", path: "/disclaimer", exportName: "DISCLAIMER", title: "Disclaimer",
    audience: "Everyone", registryDocType: null, versions: [V_2026_09_18, V_2026_08_27] },
];

export const POLICY_BY_SLUG = Object.fromEntries(POLICIES.map((p) => [p.slug, p]));

export function currentVersion(slug) {
  return POLICY_BY_SLUG[slug]?.versions?.[0] || null;
}

export function previousVersions(slug) {
  return (POLICY_BY_SLUG[slug]?.versions || []).slice(1);
}

const MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August",
                   "September", "October", "November", "December"];
const MONTHS_TL = ["Enero", "Pebrero", "Marso", "Abril", "Mayo", "Hunyo", "Hulyo", "Agosto",
                   "Setyembre", "Oktubre", "Nobyembre", "Disyembre"];

// Dates are written out by hand rather than through toLocaleDateString, so a
// visitor's browser locale can never shift an effective date by a day.
export function formatEffective(iso, lang = "en") {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const months = lang === "tl" ? MONTHS_TL : MONTHS_EN;
  return `${months[m - 1]} ${d}, ${y}`;
}
